import styles from './ContactPerson.module.scss';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { UI_Picture } from 'ui/components/1-atoms/Media/Picture/Picture';
import { UI_Paragraph } from 'ui/components/1-atoms/Paragraph/Paragraph';
import { UI_Link } from 'ui/components/1-atoms/Action/Link/Link';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import ColorOptions, { LinkType } from 'helpers/types';
import { UI_TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { UI_TopLevelGridCell } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import { UI_Grid } from 'ui/components/4-habitats/Grid/Grid';
import { UI_GridCell } from 'ui/components/4-habitats/Grid/GridCell';
import classNames from 'classnames';

export interface UI_ContactPersonProps {
	className?: string;
	bgColor?: ColorOptions;
	heading?: string;
	ctaButton?: LinkType;
	image: {
		url: string;
		altText: string;
		focalPoint?: { top: number; left: number };
		fileType?: string;
	};
	imageCropping?: 'cover' | 'contain';
	details?: { name: string; role: string; email: string; phoneNumber: string; linkedIn: { url: string; name: string } };
	globalTheme?: string;
}

export const UI_ContactPerson: React.FC<UI_ContactPersonProps> = ({
	className,
	bgColor,
	heading,
	ctaButton,
	details,
	image,
	imageCropping,
	globalTheme,
}) => {
	imageCropping = imageCropping || 'cover';

	return (
		<div className={classNames(styles.ContactPerson, styles[`u-color___${bgColor}`], className)}>
			<UI_Container width="Standard">
				<UI_TopLevelGrid container wrap>
					<UI_TopLevelGridCell desktopWidth="22col" mobileWidth="100" desktopOffset="2col" offsetDirection="right">
						<UI_Grid container wrap largeGutter>
							<UI_GridCell desktopWidth="66" mobileWidth="100">
								<div className={styles.ContactPerson_infoWrapper}>
									<div className={styles.ContactPerson_infoContainer}>
										<div>
											{heading && (
												<UI_Heading className={classNames(styles.ContactPerson_heading)} headingLevel="h2" style="md">
													{heading}
												</UI_Heading>
											)}

											{ctaButton && (
												<UI_Link
													className={styles.ContactPerson_ctaButton}
													href={ctaButton.url}
													style="lg"
													globalTheme={globalTheme}
													iconLink={globalTheme === 'kogp'}
												>
													{ctaButton.name}
												</UI_Link>
											)}
										</div>

										{image && (
											<UI_Picture
												className={styles.ContactPerson_picture}
												{...image}
												focalPoint={image?.focalPoint}
												desktopNoShow={true}
												imageCropping="cover"
												fileType={image?.fileType}
											/>
										)}

										{details && (
											<div className={styles.ContactPerson_details}>
												{(details.name || details.role) && (
													<div className={styles.ContactPerson_detailsColumn}>
														{details.name && <UI_Paragraph>{details.name}</UI_Paragraph>}
														{details.role && <UI_Paragraph>{details.role}</UI_Paragraph>}
													</div>
												)}

												{(details.email || details.phoneNumber) && (
													<div className={styles.ContactPerson_detailsColumn}>
														{details.email && <UI_Link href={`mailto:${details.email}`}>{details.email}</UI_Link>}
														{details.phoneNumber && (
															<UI_Link href={`tel:${details.phoneNumber}`}>{details.phoneNumber}</UI_Link>
														)}
													</div>
												)}

												{details.linkedIn && (
													<div className={styles.ContactPerson_detailsColumn}>
														<UI_Link href={details.linkedIn.url}>{details.linkedIn.name}</UI_Link>
													</div>
												)}
											</div>
										)}
									</div>
								</div>
							</UI_GridCell>
							<UI_GridCell desktopWidth="33" mobileWidth="100">
								{image && (
									<div className={styles.ContactPerson_imageContainer}>
										<UI_Picture
											{...image}
											focalPoint={{
												top: image.focalPoint?.top,
												left: image.focalPoint?.left,
											}}
											mobileNoShow={true}
											imageCropping={imageCropping}
											sizes="(max-width: 768px) 100vw, 460px"
											fileType={image?.fileType}
										/>
									</div>
								)}
							</UI_GridCell>
						</UI_Grid>
					</UI_TopLevelGridCell>
				</UI_TopLevelGrid>
			</UI_Container>
		</div>
	);
};
